import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { ChartProps } from './types';
import { getPerformanceLevel, calculateAbsoluteDifference } from './benchmarkThresholds';

export const ScatterPlot: React.FC<ChartProps> = ({ metrics, chartId = 'scatter' }) => {
  const chartRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const getPerformanceColors = (brandValue: number, industryMean: number) => {
    const absoluteDifference = calculateAbsoluteDifference(brandValue, industryMean);
    const perfLevel = getPerformanceLevel(absoluteDifference);

    const colorMaps = {
      'excellent': { color: '#16a34a', bg: '#dcfce7', performance: 'excellent' as const },
      'very-good': { color: '#22c55e', bg: '#ecfccb', performance: 'very-good' as const },
      'good': { color: '#4ade80', bg: '#f0fdf4', performance: 'good' as const },
      'fair': { color: '#f59e0b', bg: '#fef3c7', performance: 'fair' as const },
      'poor': { color: '#f97316', bg: '#fed7aa', performance: 'poor' as const },
      'critical': { color: '#dc2626', bg: '#fecaca', performance: 'critical' as const }
    };

    return colorMaps[perfLevel.performance];
  };

  useEffect(() => {
    if (!chartRef.current || !containerRef.current || metrics.length === 0) return;

    const container = containerRef.current;
    const svg = d3.select(chartRef.current);
    svg.selectAll('*').remove();

    const containerRect = container.getBoundingClientRect();
    const margin = { top: 40, right: 40, bottom: 60, left: 60 };
    const width = containerRect.width - margin.left - margin.right;
    const height = Math.max(400, Math.min(600, width * 0.75));

    svg.attr('width', containerRect.width).attr('height', height + margin.top + margin.bottom);

    // Create tooltip
    const tooltip = d3.select('body').selectAll(`.scatter-tooltip-${chartId}`)
      .data([null])
      .join('div')
      .attr('class', `scatter-tooltip scatter-tooltip-${chartId}`)
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('z-index', '1000')
      .style('pointer-events', 'none');

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Filter metrics with industry data
    const validMetrics = metrics.filter(d => d.industryMean && d.industryMean > 0);

    if (validMetrics.length === 0) {
      g.append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .attr('font-size', '14px')
        .attr('fill', '#6b7280')
        .text('No industry benchmark data available');
      return;
    }

    // Calculate scales
    const industryValues = validMetrics.map(d => d.industryMean || 0);
    const brandValues = validMetrics.map(d => d.value);

    const industryExtent = d3.extent(industryValues) as [number, number];
    const brandExtent = d3.extent(brandValues) as [number, number];

    const allMin = Math.min(industryExtent[0], brandExtent[0]);
    const allMax = Math.max(industryExtent[1], brandExtent[1]);
    const padding = (allMax - allMin) * 0.1;

    const xScale = d3.scaleLinear()
      .domain([allMin - padding, allMax + padding])
      .range([0, width]);

    const yScale = d3.scaleLinear()
      .domain([allMin - padding, allMax + padding])
      .range([height, 0]);

    // Add axes
    const xAxis = d3.axisBottom(xScale).tickFormat(d => `${(d as number).toFixed(0)}%`);
    const yAxis = d3.axisLeft(yScale).tickFormat(d => `${(d as number).toFixed(0)}%`);

    g.append('g')
      .attr('transform', `translate(0, ${height})`)
      .call(xAxis)
      .append('text')
      .attr('x', width / 2)
      .attr('y', 40)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('font-weight', '600')
      .attr('fill', '#374151')
      .text('Industry Average');

    g.append('g')
      .call(yAxis)
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -height / 2)
      .attr('y', -40)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('font-weight', '600')
      .attr('fill', '#374151')
      .text('Brand Performance');

    // Add reference line (y = x)
    const diagonalLine = d3.line<[number, number]>()
      .x(d => xScale(d[0]))
      .y(d => yScale(d[1]));

    g.append('path')
      .datum([[allMin - padding, allMin - padding], [allMax + padding, allMax + padding]] as Array<[number, number]>)
      .attr('d', diagonalLine)
      .attr('stroke', '#94a3b8')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '5,5')
      .attr('opacity', 0.7);

    // Add reference line label
    g.append('text')
      .attr('x', xScale(allMax + padding) - 10)
      .attr('y', yScale(allMax + padding) + 15)
      .attr('text-anchor', 'end')
      .attr('font-size', '10px')
      .attr('fill', '#94a3b8')
      .text('Parity line');

    // Add quadrant background zones
    const midPoint = (allMin + allMax) / 2;

    // Above-right quadrant (high brand, high industry) - light green
    g.append('rect')
      .attr('x', xScale(midPoint))
      .attr('y', yScale(allMax + padding))
      .attr('width', xScale(allMax + padding) - xScale(midPoint))
      .attr('height', yScale(midPoint) - yScale(allMax + padding))
      .attr('fill', '#f0fdf4')
      .attr('opacity', 0.3);

    // Below-left quadrant (low brand, low industry) - light yellow
    g.append('rect')
      .attr('x', xScale(allMin - padding))
      .attr('y', yScale(midPoint))
      .attr('width', xScale(midPoint) - xScale(allMin - padding))
      .attr('height', yScale(allMin - padding) - yScale(midPoint))
      .attr('fill', '#fefce8')
      .attr('opacity', 0.3);

    // Draw data points
    validMetrics.forEach((d) => {
      const colors = getPerformanceColors(d.value, d.industryMean || 0);
      const x = xScale(d.industryMean || 0);
      const y = yScale(d.value);

      g.append('circle')
        .attr('cx', x)
        .attr('cy', y)
        .attr('r', 6)
        .attr('fill', colors.color)
        .attr('stroke', 'white')
        .attr('stroke-width', 2)
        .attr('opacity', 0.8)
        .style('cursor', 'pointer')
        .on('mouseover', function(event) {
          const absoluteDiff = calculateAbsoluteDifference(d.value, d.industryMean || 0);
          const performanceText = colors.performance === 'excellent' ? 'Outstanding Performance' :
                                 colors.performance === 'very-good' ? 'Strong Performance' :
                                 colors.performance === 'good' ? 'Above Average' :
                                 colors.performance === 'fair' ? 'Below Average' :
                                 colors.performance === 'poor' ? 'Needs Improvement' : 'Urgent Attention Required';

          tooltip.html(`
            <div style="
              background: ${colors.bg};
              border: 2px solid ${colors.color};
              border-radius: 12px;
              padding: 16px;
              min-width: 200px;
              box-shadow: 0 8px 32px -8px rgba(0, 0, 0, 0.25);
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            ">
              <div style="font-weight: 700; font-size: 14px; color: #1e293b; margin-bottom: 8px;">
                ${d.categoryName}
              </div>
              <div style="color: #475569; margin-bottom: 8px; font-size: 12px;">
                ${performanceText}
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: #64748b; font-size: 12px;">Performance Gap:</span>
                <span style="font-weight: 600; color: ${colors.color}; font-size: 12px;">
                  ${absoluteDiff >= 0 ? '+' : ''}${absoluteDiff.toFixed(1)}%
                </span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: #64748b; font-size: 12px;">Brand Score:</span>
                <span style="font-weight: 600; color: #1e293b; font-size: 12px;">${d.value.toFixed(1)}%</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="color: #64748b; font-size: 12px;">Industry Avg:</span>
                <span style="font-weight: 600; color: #64748b; font-size: 12px;">${d.industryMean?.toFixed(1)}%</span>
              </div>
            </div>
          `)
          .style('visibility', 'visible');

          d3.select(this).attr('r', 8).attr('opacity', 1);
        })
        .on('mousemove', function(event) {
          tooltip
            .style('left', (event.pageX + 10) + 'px')
            .style('top', (event.pageY - 10) + 'px');
        })
        .on('mouseout', function() {
          tooltip.style('visibility', 'hidden');
          d3.select(this).attr('r', 6).attr('opacity', 0.8);
        });
    });

    // Add quadrant labels
    g.append('text')
      .attr('x', xScale(midPoint) + (xScale(allMax + padding) - xScale(midPoint)) / 2)
      .attr('y', yScale(midPoint) + (yScale(allMin - padding) - yScale(midPoint)) / 2)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('font-weight', '500')
      .attr('fill', '#16a34a')
      .attr('opacity', 0.7)
      .text('Leaders');

    g.append('text')
      .attr('x', xScale(midPoint) - (xScale(midPoint) - xScale(allMin - padding)) / 2)
      .attr('y', yScale(midPoint) - (yScale(midPoint) - yScale(allMax + padding)) / 2)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('font-weight', '500')
      .attr('fill', '#f59e0b')
      .attr('opacity', 0.7)
      .text('Challengers');

    // Add legend
    const legend = g.append('g')
      .attr('transform', `translate(${width - 180}, 20)`);

    legend.append('text')
      .attr('x', 0)
      .attr('y', 0)
      .attr('font-size', '12px')
      .attr('font-weight', '600')
      .attr('fill', '#374151')
      .text('Performance Matrix');

    const legendData = [
      { color: '#16a34a', label: 'Excellent (≥5%)' },
      { color: '#22c55e', label: 'Very Good (2-5%)' },
      { color: '#4ade80', label: 'Good (0-2%)' },
      { color: '#f59e0b', label: 'Fair (-2-0%)' },
      { color: '#dc2626', label: 'Poor (<-2%)' }
    ];

    legendData.forEach((item, i) => {
      const y = 20 + i * 18;

      legend.append('circle')
        .attr('cx', 6)
        .attr('cy', y)
        .attr('r', 4)
        .attr('fill', item.color);

      legend.append('text')
        .attr('x', 15)
        .attr('y', y + 1)
        .attr('font-size', '10px')
        .attr('dominant-baseline', 'middle')
        .attr('fill', '#374151')
        .text(item.label);
    });

    // Cleanup function
    return () => {
      d3.select('body').select(`.scatter-tooltip-${chartId}`).remove();
    };
  }, [metrics, chartId]);

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <svg ref={chartRef}></svg>
    </div>
  );
};